import {Injectable} from '@angular/core';
import {HttpClient} from '@angular/common/http';
import {FileEsolver} from '../_models/FileEsolver';
import {HubConnection} from '@microsoft/signalr';
import {buildHubConnection, startHubConnection, stopHubConnection} from '../_helpers/hub-connection.helper';
import {HubState} from '../_models/hub-state';
import {environment} from '../../environments/environment';
import {BehaviorSubject, firstValueFrom} from 'rxjs';
import {OrganizzazioniService} from './organizzazioni.service';
import {SaveMessage} from '../_helpers/save-message';
import {Ordine} from '../_models/Ordine';
import {Organizzazione} from '../_models/Organizzazione';
import {DatePipe} from '@angular/common';
import {OrdineGroup} from '../_models/OrdineGroup';
import {FilterItem} from '../_models/FilterItem';
import {OrdineByBarcode} from '../_models/OrdineByBarcode';
import {SearchResult} from '../_models/SearchResult';
import {Evento} from "../_models/Evento";
import {Contenitore} from '../_models/Contenitore';
import {
  FaseCorrenteRef,
  FasePrecedenteAperta,
  FineConPrecedentiResult
} from '../_models/FasePrecedenteAperta';
import {OrdineKey} from '../_models/Ordine';
import {ReinviaCaricoResult} from '../_models/ReinviaCaricoResult';
import {ContenitoreInfo} from '../_models/ContenitoreInfo';
import {DettaglioPercorso} from '../_models/AccountOrganizzazione';

const cachePrefix = 'archiconnect-v2-';
const datepipe: DatePipe = new DatePipe('en-US');

@Injectable({providedIn: 'root'})
export class AvanzamentoService {
  hubState: BehaviorSubject<number> = new BehaviorSubject<number>(HubState.Connecting);
  public lastAvanzamentoCreated: BehaviorSubject<FileEsolver> = new BehaviorSubject<FileEsolver>(null);
  public activeTabTipoQta: BehaviorSubject<number> = new BehaviorSubject<number>(0);
  private hubConnection: HubConnection;
  private suppressedHubInfoByOrder = new Set<string>();

  constructor(
    private saveMessage: SaveMessage,
    private organizzazioniService: OrganizzazioniService,
    private http: HttpClient) {
    const tipoQta = JSON.parse(localStorage.getItem(cachePrefix + 'TipoQta')) || 0;
    this.activeTabTipoQta.next(tipoQta);
  }

  public startConnection() {
    this.hubConnection = buildHubConnection(`${environment.localApiUrl}hubs/avanzamento`, this.hubState);

    this.hubConnection.on('SendCreated', (file: FileEsolver) => {
      if (file.organizzazione.id === this.organizzazioniService.organizzazioneId) {
        this.saveMessage.messageInfo('Avanzamento inviato ad ESOLVER.');
        this.lastAvanzamentoCreated.next(file);
      }
    });

    return startHubConnection(this.hubConnection, this.hubState);
  }

  public stopConnection() {
    void stopHubConnection(this.hubConnection);
  }

  async invioFileEsolver(avanzamento: FileEsolver) {
    return firstValueFrom(
      this.http.post<FileEsolver>(`${environment.localApiUrl}api/Avanzamento/InvioAvanzamento`, avanzamento)
    );
  }

  async invioInizioGroup(orgId: number, dataEvento: number, ordineGroup: OrdineGroup, username: string) {
    return firstValueFrom(this.http.post<OrdineGroup>(`${environment.localApiUrl}api/Avanzamento/InvioInizioGroup`,
      {orgId, dataEvento, ordineGroup, username}));
  }

  async invioPausaGroup(orgId: number, dataEvento: number, ordineGroup: OrdineGroup, lavorataManuale: number,
                        scartataManuale: number, nonConformeManuale: number, username: string,
                        suppressHubInfoNotification = false) {
    const suppressionKey = this.trackHubInfoSuppression(ordineGroup, suppressHubInfoNotification);
    return firstValueFrom(this.http.post<OrdineGroup>(`${environment.localApiUrl}api/Avanzamento/InvioPausaGroup`,
      {orgId, dataEvento, ordineGroup, lavorataManuale, scartataManuale, nonConformeManuale, username}))
      .catch((error) => {
        this.releaseHubInfoSuppression(suppressionKey);
        throw error;
      });
  }

  async invioAvanzaGroup(orgId: number, dataEvento: number, ordineGroup: OrdineGroup, lavorataManuale: number,
                         scartataManuale: number, nonConformeManuale: number, username: string,
                         suppressHubInfoNotification = false) {
    const suppressionKey = this.trackHubInfoSuppression(ordineGroup, suppressHubInfoNotification);
    return firstValueFrom(this.http.post<OrdineGroup>(`${environment.localApiUrl}api/Avanzamento/InvioAvanzaGroup`,
      {orgId, dataEvento, ordineGroup, lavorataManuale, scartataManuale, nonConformeManuale, username}))
      .catch((error) => {
        this.releaseHubInfoSuppression(suppressionKey);
        throw error;
      });
  }

  async invioAvanzaQualitaGroup(orgId: number, dataEvento: number, ordineGroup: OrdineGroup, lavorataManuale: number,
                                scartataManuale: number, nonConformeManuale: number, username: string,
                                suppressHubInfoNotification = false) {
    const suppressionKey = this.trackHubInfoSuppression(ordineGroup, suppressHubInfoNotification);
    return firstValueFrom(this.http.post<OrdineGroup>(`${environment.localApiUrl}api/Avanzamento/InvioAvanzaQualitaGroup`,
      {orgId, dataEvento, ordineGroup, lavorataManuale, scartataManuale, nonConformeManuale, username}))
      .catch((error) => {
        this.releaseHubInfoSuppression(suppressionKey);
        throw error;
      });
  }

  async invioFineGroup(orgId: number, dataEvento: number, ordineGroup: OrdineGroup, chiudiQtaFine: boolean, username: string,
                       suppressHubInfoNotification = false) {
    const suppressionKey = this.trackHubInfoSuppression(ordineGroup, suppressHubInfoNotification);
    return firstValueFrom(this.http.post<OrdineGroup>(`${environment.localApiUrl}api/Avanzamento/InvioFineGroup`,
      {orgId, dataEvento, ordineGroup, lavorataManuale: 0, scartataManuale: 0, chiudiQtaFine, username}))
      .catch((error) => {
        this.releaseHubInfoSuppression(suppressionKey);
        throw error;
      });
  }

  /**
   * Le righe/fasi a monte ancora aperte (issue #3477).
   *
   * ⚠️ Prende **l'elenco** delle fasi del gruppo, non una terna: il gruppo di lavoro non è omogeneo
   * per documento e il perimetro è l'unione dei perimetri di tutte.
   */
  async getFasiPrecedentiAperte(orgId: number, fasiCorrenti: FaseCorrenteRef[])
    : Promise<SearchResult<FasePrecedenteAperta>> {
    return firstValueFrom(this.http.post<SearchResult<FasePrecedenteAperta>>(
      `${environment.localApiUrl}api/Avanzamento/FasiPrecedentiAperte`, {orgId, fasiCorrenti}));
  }

  /**
   * Termina la fase corrente e poi, una per una, le fasi precedenti selezionate.
   *
   * ⚠️ Nessun timeout: la chiusura di N fasi è sequenziale e dura in proporzione. Una rinuncia
   * anticipata lascerebbe l'operatore senza esiti mentre le chiusure proseguono sul server — cioè
   * nella condizione in cui rifarebbe l'operazione e produrrebbe doppi carichi.
   */
  async invioFineGroupConPrecedenti(orgId: number, dataEvento: number, ordineGroup: OrdineGroup,
                                    chiudiQtaFine: boolean, username: string, fasiPrecedenti: OrdineKey[],
                                    suppressHubInfoNotification = false) {
    const suppressionKey = this.trackHubInfoSuppression(ordineGroup, suppressHubInfoNotification);
    return firstValueFrom(this.http.post<FineConPrecedentiResult>(
      `${environment.localApiUrl}api/Avanzamento/InvioFineGroupConPrecedenti`,
      {orgId, dataEvento, ordineGroup, lavorataManuale: 0, scartataManuale: 0, chiudiQtaFine, username, fasiPrecedenti}))
      .catch((error) => {
        this.releaseHubInfoSuppression(suppressionKey);
        throw error;
      });
  }

  /**
   * Riaccoda verso ESOLVER un carico rimasto senza file (issue #3512).
   *
   * ⛔ Non è ripetibile: finché il nuovo evento di invio non compare, il servizio non ha modo di
   * riconoscere un file già scritto e una seconda chiamata ne produrrebbe un altro. Fuori dalla
   * finestra oraria configurata sull'organizzazione quell'attesa può durare ore.
   *
   * ⚠️ La conferma modale e la disabilitazione dopo l'accodamento **riducono** il rischio, non lo
   * chiudono: vivono nell'istanza del componente, quindi un ricaricamento della pagina o una
   * seconda scheda le azzerano. La chiusura vera richiede idempotenza sulla coda o la persistenza
   * del nome file, ed è dichiarata fuori scopo (rischio R-2).
   */
  async reinviaCaricoAsync(orgId: number, idEvento: number, username: string): Promise<ReinviaCaricoResult> {
    return firstValueFrom(this.http.post<ReinviaCaricoResult>(
      `${environment.localApiUrl}api/Avanzamento/ReinviaCarico`, {orgId, idEvento, username}));
  }

  async stampaScartoGroup(orgId: number, dataEvento: number, ordineGroup: OrdineGroup, username: string) {
    const suppressionKey = this.trackHubInfoSuppression(ordineGroup, false);
    return firstValueFrom(this.http.post<OrdineGroup>(`${environment.localApiUrl}api/Avanzamento/StampaScartoGroup`,
      {orgId, dataEvento, ordineGroup, username}))
      .catch((error) => {
        this.releaseHubInfoSuppression(suppressionKey);
        throw error;
      });
  }

  async checkContenitoreForOrdine(orgId: number, codBox: string, ordine: Ordine): Promise<Contenitore> {
    return firstValueFrom(
      this.http.post<Contenitore>(
        `${environment.localApiUrl}api/Avanzamento/CheckContenitore?orgId=${orgId}&codBox=${codBox}`,
        ordine
      )
    );
  }

  async undoPlanGroup(orgId: number, dataEvento: number, ordineGroup: OrdineGroup, lavorataManuale: number, scartataManuale: number, username: string) {
    return firstValueFrom(this.http.post<OrdineGroup>(`${environment.localApiUrl}api/Avanzamento/UndoPlanGroup`,
      {orgId, dataEvento, ordineGroup, lavorataManuale, scartataManuale, username}));
  }

  async getOrdiniByBarcode(orgId: number, saldato: boolean, filters: FilterItem[], filtroOrdini: number) {
    return firstValueFrom(this.http.post<OrdineByBarcode>(`${environment.localApiUrl}api/Avanzamento/GetOrdiniByBarcode`,
      {orgId, saldato, filters, search: '', sortField: '', sortAsc: false, group: '', filtroOrdini}));
  }

  async getAvanzamentiOrdine(orgId: number, idDocumento: number, idRiga: number, idRigaLavorazione: number, codBox: string) {
    return firstValueFrom(this.http.post<SearchResult<Evento>>(`${environment.localApiUrl}api/Avanzamento/GetAvanzamentiOrdine`,
      {orgId, idDocumento, idRiga, idRigaLavorazione, codBox}));
  }

  async getFasiByDocumento(orgId: number, idDocumento: number, codArt: string, idRiga: number): Promise<SearchResult<Ordine>> {
    return firstValueFrom(
      this.http.post<SearchResult<Ordine>>(
        `${environment.localApiUrl}api/Ordini/FasiByDocumento`,
        {orgId, idDocumento, codArt, idRiga}
      )
    );
  }

  async getContenitoreByBox(orgId: number, idDocumento: number, idRiga: number, codBox: string): Promise<Contenitore[]> {
    return firstValueFrom(
      this.http.get<Contenitore[]>(
        `${environment.localApiUrl}api/Avanzamento/GetContenitoreByBox`,
        {params: {orgId, idDocumento, idRiga, codBox}}
      )
    );
  }

  // Feature 162: sola lettura, nessun get-or-create del contenitore.
  // Feature 166: dettaglio del percorso (preferenza operatore); assente = Attuale e prossima.
  async getContenitoreInfo(orgId: number, codBox: string, filtroOrdini: number,
                           dettaglio = DettaglioPercorso.AttualeProssima): Promise<ContenitoreInfo> {
    return firstValueFrom(
      this.http.post<ContenitoreInfo>(
        `${environment.localApiUrl}api/Avanzamento/ContenitoreInfo/${orgId}`,
        {codBox, filtroOrdini, dettaglio, page: 0, count: 0}
      )
    );
  }

  invioFileEsolverFromOrdine(ordine: Ordine, dataEvento: Date, currentOrganizzazioneValue: Organizzazione, aggLottoScarico: string, aggSaldatoRiga: boolean) {
    const rifIdDocumento = this.padLeft(ordine.idDocumento, 9);
    const rifIdRiga = this.padLeft(ordine.idRiga, 4);
    const rifIdRigaLavorazione = this.padLeft(ordine.idRigaLavorazione, 4);

    if (ordine.quantitaLavorataWait == null) {
      ordine.quantitaLavorataWait = 0;
    }
    if (ordine.quantitaNonConformeWait == null) {
      ordine.quantitaNonConformeWait = 0;
    }
    if (ordine.qtaRimanente == null) {
      ordine.qtaRimanente = ordine.quantita;
    }

    const ordineFileEsolver: FileEsolver = {
      organizzazione: currentOrganizzazioneValue,
      lavorazioni: {
        idDocumento: ordine.idDocumento,
        idRiga: ordine.idRiga,
        idRigaLavorazione: ordine.idRigaLavorazione,
        codiceArticolo: ordine.codiceArticolo,
        quantita: ordine.quantita,
        quantitaLavorata: ordine.quantitaLavorataWait + ordine.quantitaNonConformeWait,
        quantitaScartata: ordine.quantitaScartataWait,
        tempo: '0',
        risorsa: ordine?.risorsa?.codice ?? '',
        lavorazione: ordine?.lavorazione?.codice ?? '',
        rifOrdine: rifIdDocumento + rifIdRiga + rifIdRigaLavorazione,
        saldato: ordine.flgTerminato,
        saldatoRiga: aggSaldatoRiga,
        dataRegistrazione: datepipe.transform(dataEvento.toISOString(), 'ddMMyyyy'),
        lottoScarico: aggLottoScarico
      },
      ordine
    };

    return this.invioFileEsolver(ordineFileEsolver);
  }

  isNumber = (value: number) => typeof value === 'number' && isFinite(value);

  padLeft(num: number, size: number) {
    let s = num + '';
    while (s.length < size) {
      s = '0' + s;
    }
    return s;
  }

  private trackHubInfoSuppression(ordineGroup: OrdineGroup, suppress: boolean): string | undefined {
    if (!suppress) {
      return undefined;
    }

    const key = this.getOrderNotificationKey(
      ordineGroup?.organizzazioneId,
      ordineGroup?.idDocumento,
      ordineGroup?.idRiga,
      ordineGroup?.idRigaLavorazione
    );

    if (key) {
      this.suppressedHubInfoByOrder.add(key);
    }
    return key;
  }

  private releaseHubInfoSuppression(key?: string) {
    if (!key) {
      return;
    }
    this.suppressedHubInfoByOrder.delete(key);
  }

  private getOrderNotificationKey(
    orgId: number | undefined,
    idDocumento: number | undefined,
    idRiga: number | undefined,
    idRigaLavorazione: number | undefined
  ): string | undefined {
    if (!this.isFiniteNumber(orgId) || !this.isFiniteNumber(idDocumento) ||
      !this.isFiniteNumber(idRiga) || !this.isFiniteNumber(idRigaLavorazione)) {
      return undefined;
    }

    return `${orgId}:${idDocumento}:${idRiga}:${idRigaLavorazione}`;
  }

  private isFiniteNumber(value: unknown): value is number {
    return typeof value === 'number' && Number.isFinite(value);
  }
}
